import React, { useState } from "react";
import { CHART_3, INK, MUTED, GRID, PAPER, ramp } from "./palette";
import ModeWord from "./ModeWord";

// ---- partial_scatter: a partial correlation as two scatterplots side by side ----
// Props from ChartData#partial_scatter: raw [[x, y]] as measured, leftovers [[x residual, y residual]]
// after a straight line on the control variable(s), control [the first control's value per case],
// r (as measured) and partialR (from the recompute), rawSlope and leftoverSlope (each panel's
// least-squares line), xName, yName, controlNames, xUnit/yUnit/controlUnit ("words correct").
// Points are shaded by the control on ConceptChart's heatmap ramp (near-white → chart-1), starting
// partway up so the lightest dots still show; chart-3 draws the lines so they stand out from the dots.
// Tokens, axes and tooltips follow ConceptChart.jsx.
const RAMP_START = 0.15;
const W = 640, TOP = 16, RIGHT = 12, GAP = 28;

const fmt = (x, places = 2) => (x < 0 ? "−" : "") + Math.abs(x).toFixed(places);
const fmtR = (x, places = 2) => fmt(x, places).replace(/^(−?)0\./, "$1.");
const minus = (t) => (t < 0 ? `−${-t}` : t);
const scale = (ax, a, b) => (v) => a + ((v - ax.min) / (ax.max - ax.min)) * (b - a);
const lowerWords = (s) => s.split(" ").map((w) => (/^[A-Z][a-z]+$/.test(w) ? w.toLowerCase() : w)).join(" ");
const sentenceList = (parts) => (parts.length < 2 ? parts.join("") : `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`);
const IRREGULAR = { child: "children", person: "people" };
const plural = (noun) => IRREGULAR[noun] || `${noun}s`;
const mean = (xs) => xs.reduce((a, b) => a + b, 0) / xs.length;
const cor = (a, b) => {
	const ma = mean(a), mb = mean(b);
	const sab = a.reduce((s, v, i) => s + (v - ma) * (b[i] - mb), 0);
	return sab / Math.sqrt(a.reduce((s, v) => s + (v - ma) ** 2, 0) * b.reduce((s, v) => s + (v - mb) ** 2, 0));
};
// "words correct" → "words": the unit's first word, for "2.3 words above what age predicts".
const word = (unit) => (unit ? unit.split(" ")[0] : "");
const shade = (t) => {
	const u = RAMP_START + (1 - RAMP_START) * Math.max(0, Math.min(1, t));
	return ramp(u);
};

// Nice bounds and ticks (as ConceptChart's axis()), allowing values either side of zero.
function axis(xs, pad = 0.05) {
	const lo = Math.min(...xs), hi = Math.max(...xs), span = hi - lo || 1;
	const rough = span / 5, mag = 10 ** Math.floor(Math.log10(rough));
	const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= rough);
	const min = Math.floor((lo - span * pad) / step) * step, max = Math.ceil((hi + span * pad) / step) * step;
	const ticks = [];
	for (let t = min; t <= max + step / 2; t += step) ticks.push(+t.toFixed(10));
	return { min, max, ticks };
}

function Tooltip({ x, lines }) {
	const longest = Math.max(...lines.map((l) => String(l).length));
	const width = Math.max(170, Math.round(longest * 6.4) + 16), left = Math.min(Math.max(x + 8, 16), W - RIGHT - width);
	return (
		<g pointerEvents="none" transform={`translate(${left},${TOP})`}>
			<rect width={width} height={10 + lines.length * 15} rx="3" fill={PAPER} stroke={GRID} />
			{lines.map((line, i) => (
				<text key={i} x="8" y={17 + i * 15} fontSize="11" fill={i === 0 ? MUTED : INK} fontWeight={i === 0 ? 400 : 600}>{line}</text>
			))}
		</g>
	);
}

export default function PartialScatterChart({ raw, leftovers, control, r, partialR, rawSlope, leftoverSlope, xName, yName, controlNames = [],
	xUnit, yUnit, controlUnit, noun = "participant" }) {
	const [hover, setHover] = useState(null);
	const height = 360;
	const pw = (W - GAP) / 2;
	const zMin = Math.min(...control), zMax = Math.max(...control);
	const tOf = (z) => (zMax > zMin ? (z - zMin) / (zMax - zMin) : 0.5);
	const controlName = controlNames[0] || "the control";
	const others = sentenceList(controlNames.map(lowerWords));
	const x1 = lowerWords(xName), y1 = lowerWords(yName), z1 = lowerWords(controlName);
	const away = (v, unit) => `${fmt(Math.abs(v))}${unit ? ` ${word(unit)}` : ""} ${v < 0 ? "below" : "above"} what ${others} predict${controlNames.length > 1 ? "" : "s"}`;
	const zText = (z) => `${z1} ${z}${controlUnit && !/^years?$/.test(controlUnit) ? ` ${controlUnit}` : ""}`;

	// Where the control's high values sit in the left panel, for the caption.
	const rx = cor(control, raw.map((p) => p[0])), ry = cor(control, raw.map((p) => p[1]));
	const corner = Math.abs(rx) >= 0.1 && Math.abs(ry) >= 0.1 ? `${ry > 0 ? "top" : "bottom"} ${rx > 0 ? "right" : "left"}` : null;

	const panels = [
		{ key: "raw", points: raw, slope: rawSlope, title: `As measured: r = ${fmtR(r)}`,
			xLabel: `${xName}${xUnit ? ` (${xUnit})` : ""}`, yLabel: `${yName}${yUnit ? ` (${yUnit})` : ""}`,
			tip: (i) => [`One ${noun}, ${zText(control[i])}`, `${xName}: ${raw[i][0]}`, `${yName}: ${raw[i][1]}`] },
		{ key: "leftovers", points: leftovers, slope: leftoverSlope, title: `Left over after ${others}: partial r = ${fmtR(partialR)}`, zero: true,
			xLabel: `${xName} left over${xUnit ? ` (${word(xUnit)})` : ""}`, yLabel: `${yName} left over${yUnit ? ` (${word(yUnit)})` : ""}`,
			tip: (i) => [`One ${noun}, ${zText(control[i])}`, `${xName}: ${away(leftovers[i][0], xUnit)}`, `${yName}: ${away(leftovers[i][1], yUnit)}`] }
	];
	const lineTip = (p) => p.key === "raw"
		? [`Best-fitting line, as measured`, `Slope ${fmt(p.slope, 3)}${yUnit ? ` ${word(yUnit)}` : ""} per ${xUnit ? word(xUnit).replace(/s$/, "") : "unit"} of ${x1}`]
		: [`Best-fitting line through the leftovers`, `Slope ${fmt(p.slope, 3)}${yUnit ? ` ${word(yUnit)}` : ""} per ${xUnit ? word(xUnit).replace(/s$/, "") : "unit"} of ${x1}`,
			`= ${x1}'s coefficient for ${y1} with ${others} in the regression`];

	return (
		<figure>
			<div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-gray-600">
				<span className="flex items-center gap-1.5">
					One {noun}, shaded by {z1}{controlUnit ? ` (${controlUnit})` : ""}:
					<span>{zMin}</span>
					<span className="inline-block h-2.5 w-16 rounded-sm" style={{ background: `linear-gradient(in srgb to right, ${shade(0)}, ${shade(1)})` }} />
					<span>{zMax}</span>
				</span>
				<span className="flex items-center gap-1.5"><span className="inline-block h-0.5 w-4 rounded" style={{ background: CHART_3 }} />Best-fitting line in each panel</span>
			</div>
			<svg viewBox={`0 0 ${W} ${height}`} className="mt-2 w-full" role="img"
				aria-label={`Two scatterplots of ${raw.length} ${plural(noun)}, shaded by ${z1}. Left: ${x1} against ${y1} as measured, r = ${fmtR(r)}. Right: what's left of each after a straight line on ${others}, against each other; their correlation is the partial correlation, ${fmtR(partialR)}.`}>
				{panels.map((p, pi) => {
					const ox = pi * (pw + GAP);
					const box = { l: ox + 40, r: ox + pw - 14, t: TOP + 44, b: height - 42 };
					const xs = p.points.map((q) => q[0]), ys = p.points.map((q) => q[1]);
					const axX = axis(xs), axY = axis(ys);
					const x = scale(axX, box.l, box.r), y = scale(axY, box.b, box.t);
					const mx = mean(xs), my = mean(ys);
					const lo = Math.min(...xs), hi = Math.max(...xs);
					const at = (v) => my + p.slope * (v - mx);
					return (
						<g key={p.key}>
							<text x={ox} y={TOP} fontSize="12" fill={INK} fontWeight="600">{p.title}</text>
							<text x={ox} y={box.t - 12} fontSize="11" fill={MUTED}>{p.yLabel}</text>
							{axY.ticks.map((t) => (
								<g key={`y${t}`}>
									<line x1={box.l} x2={box.r} y1={y(t)} y2={y(t)} stroke={GRID} />
									<text x={box.l - 6} y={y(t)} fontSize="11" fill={MUTED} textAnchor="end" dominantBaseline="middle">{minus(t)}</text>
								</g>
							))}
							{axX.ticks.map((t) => <text key={`x${t}`} x={x(t)} y={box.b + 16} fontSize="11" fill={MUTED} textAnchor="middle">{minus(t)}</text>)}
							<text x={box.r} y={box.b + 34} fontSize="11" fill={MUTED} textAnchor="end">{p.xLabel}</text>
							{p.zero && <line x1={x(0)} x2={x(0)} y1={box.t} y2={box.b} stroke={MUTED} strokeWidth="1" strokeDasharray="4 3" opacity="0.6" />}
							{p.zero && <line x1={box.l} x2={box.r} y1={y(0)} y2={y(0)} stroke={MUTED} strokeWidth="1" strokeDasharray="4 3" opacity="0.6" />}
							{p.points.map(([a, b], i) => (
								<circle key={i} cx={x(a)} cy={y(b)} r="4" fill={shade(tOf(control[i]))} stroke={PAPER} strokeWidth="1"
									onMouseEnter={() => setHover({ i, cx: x(a), lines: p.tip(i) })} onMouseLeave={() => setHover(null)} />
							))}
							<g onMouseEnter={() => setHover({ cx: x(hi), lines: lineTip(p) })} onMouseLeave={() => setHover(null)}>
								<line x1={x(lo)} x2={x(hi)} y1={y(at(lo))} y2={y(at(hi))} stroke="transparent" strokeWidth="10" />
								<line x1={x(lo)} x2={x(hi)} y1={y(at(lo))} y2={y(at(hi))} stroke={CHART_3} strokeWidth="2" />
							</g>
							{hover && hover.i !== undefined && (
								<circle cx={x(p.points[hover.i][0])} cy={y(p.points[hover.i][1])} r="7" fill="none" stroke={INK} strokeWidth="1.5" pointerEvents="none" />
							)}
						</g>
					);
				})}
				{hover && <Tooltip x={hover.cx} lines={hover.lines} />}
			</svg>
			<figcaption className="mt-1 text-xs text-gray-600">
				Each dot is one {noun}, <ModeWord light="darker" dark="brighter" /> for a higher {z1}; pointing at a dot rings the same {noun} in both panels.
				Left: {x1} against {y1} as measured, r = {fmtR(r)}.
				{corner ? ` ${plural(noun).charAt(0).toUpperCase() + plural(noun).slice(1)} with a higher ${z1} sit toward the ${corner}, so part of that ${fmtR(r)} is the straight-line trend both scores share with ${z1}.` : ""}
				{" "}Right: what's left of each score once its straight-line trend with {others} is taken out (0 is exactly what {others} predict{controlNames.length > 1 ? "" : "s"}).
				The shading no longer runs along the diagonal, and the correlation of these leftovers is the partial correlation, {fmtR(partialR)}.
			</figcaption>
		</figure>
	);
}
